import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiProveedoresInstance, authHeaderProveedores } from "../../redux/actions/proveedores/proveedores";

import { Form, Input, Button, Row, Col, Typography, Space, InputNumber, Switch } from "antd";
import { toast } from "../../components/toasts/toastStore";

import "./EventosPage.css";
import "./PaquetesPage.css";

const { Title, Text } = Typography;

export default function CrearProveedorPage() {
  const navigate = useNavigate();
  const { idProveedor } = useParams();
  const isEditing = !!idProveedor;

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const daCredito = Form.useWatch("da_credito", form);

  useEffect(() => {
    if (!isEditing) return;
    apiProveedoresInstance.get(`/proveedores/${idProveedor}`, { headers: authHeaderProveedores() })
      .then((res) => {
        const p = res.data ?? {};
        form.setFieldsValue({
          clave_proveedor: p.clave_proveedor,
          nombre: p.nombre,
          linea_surte: p.linea_surte,
          telefono: p.telefono,
          contacto: p.contacto,
          rfc: p.rfc,
          direccion: p.direccion,
          da_credito: Boolean(p.da_credito),
          plazo_dias_credito: p.plazo_dias_credito,
          nota: p.nota,
        });
      })
      .catch(() => toast.error("No se pudo cargar el proveedor"));
  }, [idProveedor]);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload = {
      clave_proveedor: values.clave_proveedor?.trim() || null,
      nombre: values.nombre?.trim(),
      linea_surte: values.linea_surte?.trim() || null,
      telefono: values.telefono?.trim() || null,
      contacto: values.contacto?.trim() || null,
      rfc: values.rfc?.trim() || null,
      direccion: values.direccion?.trim() || null,
      da_credito: Boolean(values.da_credito),
      plazo_dias_credito: values.da_credito ? values.plazo_dias_credito ?? null : null,
      nota: values.nota?.trim() || null,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await apiProveedoresInstance.patch(`/proveedores/${idProveedor}`, payload, { headers: authHeaderProveedores() });
        toast.success("Proveedor actualizado correctamente");
      } else {
        await apiProveedoresInstance.post("/proveedores", payload, { headers: authHeaderProveedores() });
        toast.success("Proveedor creado exitosamente");
      }
      navigate("/app/proveedores");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="eventos-title" style={{ marginBottom: 0 }}>
              {isEditing ? "Editando proveedor" : "Nuevo proveedor"}
            </Title>
            <Text className="eventos-subtitle">
              {isEditing
                ? "Modifica los datos del proveedor y guarda los cambios."
                : "Completa los datos del proveedor para registrarlo en el sistema."}
            </Text>
          </Space>

          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate("/app/proveedores")} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="primary"
              loading={saving}
              icon={!isEditing ? <i className="fa-solid fa-plus" /> : null}
              onClick={handleSave}
              style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
            >
              {isEditing ? "Guardar cambios" : "Crear proveedor"}
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical" initialValues={{ da_credito: false }}>
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-truck-field"></i></span>
              Datos del proveedor
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="nombre"
                  label={<span className="paq-field-label">Nombre del proveedor</span>}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input placeholder="Ej. Discos y Mas SA" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="clave_proveedor" label={<span className="paq-field-label">Clave (opcional)</span>}>
                  <Input placeholder="Ej. DYM-01" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="linea_surte" label={<span className="paq-field-label">Linea que surte</span>}>
                  <Input placeholder="Ej. Vinilos, papeleria..." autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="contacto" label={<span className="paq-field-label">Persona de contacto</span>}>
                  <Input placeholder="Nombre del contacto" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="telefono" label={<span className="paq-field-label">Telefono</span>}>
                  <Input placeholder="Telefono de contacto" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="rfc" label={<span className="paq-field-label">RFC</span>}>
                  <Input placeholder="RFC del proveedor" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item name="direccion" label={<span className="paq-field-label">Direccion</span>}>
                  <Input placeholder="Direccion completa" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="da_credito" label={<span className="paq-field-label">Da credito a la tienda</span>} valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              {daCredito && (
                <Col xs={24} md={12}>
                  <Form.Item name="plazo_dias_credito" label={<span className="paq-field-label">Plazo de credito (dias)</span>}>
                    <InputNumber style={{ width: "100%" }} min={0} placeholder="30" />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item name="nota" label={<span className="paq-field-label">Nota</span>}>
                  <Input.TextArea rows={3} placeholder="Notas adicionales sobre el proveedor..." autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </main>
  );
}
